import React, { createContext, useCallback, useEffect, useState } from 'react';

const CHECK_INTERVAL_MS = 2000;

export interface BrowserTabsContextType {
  tabs: Window[];
  count: number;
  openTab: (url: string) => void;
  refresh: () => void;
  openedPairIds: Set<number>;
  markPairAsOpened: (pairId: number) => void;
  clearOpenedPairs: () => void;
}

const filterOpenTabs = (tabList: Window[]) =>
  tabList.filter((tab) => !tab.closed);

// Module-level storage to persist tabs across component remounts
let globalTabs: Window[] = [];
let globalOpenedPairIds = new Set<number>();

export const BrowserTabsContext = createContext<BrowserTabsContextType | undefined>(undefined);

export const BrowserTabsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isBrowser = typeof window !== 'undefined';
  const [tabs, setTabs] = useState<Window[]>(globalTabs);
  const [openedPairIds, setOpenedPairIds] = useState<Set<number>>(globalOpenedPairIds);

  const refresh = useCallback(() => {
    if (!isBrowser) return;

    setTabs((current) => {
      const filtered = filterOpenTabs(current);
      globalTabs = filtered;
      return filtered;
    });
  }, [isBrowser]);

  const openTab = useCallback(
    (url: string) => {
      if (!isBrowser) return;

      const newTab = window.open(url, '_blank');
      if (!newTab || newTab.closed) {
        console.error(`Failed to open tab with url: ${url}`);
        return;
      }

      setTabs((current) => {
        const openTabs = filterOpenTabs(current);

        if (openTabs.includes(newTab)) {
          globalTabs = openTabs;
          return openTabs;
        }

        const updated = [...openTabs, newTab];
        globalTabs = updated;
        return updated;
      });
    },
    [isBrowser],
  );

  // Initialize and refresh tabs on mount
  useEffect(() => {
    if (!isBrowser) return;
    refresh();
  }, [isBrowser, refresh]);

  // Set up interval to refresh tabs periodically
  useEffect(() => {
    if (!isBrowser) return;

    const intervalId = window.setInterval(() => {
      refresh();
    }, CHECK_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isBrowser, refresh]);

  const markPairAsOpened = useCallback((pairId: number) => {
    setOpenedPairIds((current) => {
      const newSet = new Set(current);
      newSet.add(pairId);
      globalOpenedPairIds = newSet;
      return newSet;
    });
  }, []);

  const clearOpenedPairs = useCallback(() => {
    const cleared = new Set<number>();
    globalOpenedPairIds = cleared;
    setOpenedPairIds(cleared);
  }, []);

  const value: BrowserTabsContextType = {
    tabs,
    count: tabs.length,
    openTab,
    refresh,
    openedPairIds,
    markPairAsOpened,
    clearOpenedPairs,
  };

  return (
    <BrowserTabsContext.Provider value={value}>
      {children}
    </BrowserTabsContext.Provider>
  );
};
